/**
 * GroupPnLDetail — full P&L statement.
 *
 * v2 (demo polish):
 *   - Accepts optional `businessId` to scope the P&L to one business
 *   - All numerics defended with `?? 0` so NaN can't leak to the UI
 *   - Header label adapts to scope (Group vs. specific business)
 *   - Per-business breakdown hidden when scoped to a single business
 */
import React from "react";
import GroupCompletenessNotice, { type GroupReportMeta } from "@/components/group/GroupCompletenessNotice";
import {
  TrendingUp,
  TrendingDown,
  Loader2,
  AlertTriangle,
  Building2,
} from "lucide-react";
import { useGroupPnL } from "@/hooks/useGroupReports";
import ReportExportMenu from "./ReportExportMenu";
import { useDisplayCurrency } from "@/context/DisplayCurrencyContext";

interface Props {
  from: string;
  to: string;
  /** When set, scope to one business. Omit for group-wide. */
  businessId?: string | null;
  businessLabel?: string | null;
}

/** Safely coerce any value to a number. NaN/undefined/null → 0. */
function num(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

const GroupPnLDetail: React.FC<Props> = ({
  from,
  to,
  businessId,
  businessLabel,
}) => {
  const { data, isLoading, isError } = useGroupPnL({ from, to, businessId });
  const { format } = useDisplayCurrency();

  const dateRangeLabel = `${formatDate(from)} – ${formatDate(to)}`;
  const scopeLabel =
    businessId && businessLabel ? businessLabel : "All businesses";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-md border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5" />
        <div>
          <div className="font-medium">Couldn't load P&amp;L</div>
          <div className="opacity-80 mt-1">
            Try a different date range, or refresh.
          </div>
        </div>
      </div>
    );
  }

  // All numerics defended
  const revenueTotal = num(data.revenue?.total);
  const cogsTotal = num(data.cogs?.total);
  const expensesTotal = num(data.expenses?.total);
  const operatingTotal = Math.max(0, expensesTotal - cogsTotal);
  const grossProfit = num(data.grossProfit ?? revenueTotal - cogsTotal);
  const netProfit = num(data.netProfit);
  const isProfitable = netProfit >= 0;
  const grossMargin =
    revenueTotal > 0 ? Math.round((grossProfit / revenueTotal) * 1000) / 10 : 0;
  const netMargin =
    revenueTotal > 0 ? Math.round((netProfit / revenueTotal) * 1000) / 10 : 0;

  const revenueAccounts = (data.revenue?.accounts || []).map((a) => ({
    code: a.code || "—",
    name: a.name || "(unnamed)",
    amountMinor: num(a.amountMinor),
  }));
  const cogsAccounts = (data.cogs?.accounts || []).map((a) => ({
    code: a.code || "—",
    name: a.name || "(unnamed)",
    amountMinor: num(a.amountMinor),
  }));
  const expenseAccounts = (data.expenses?.accounts || []).map((a) => ({
    code: a.code || "—",
    name: a.name || "(unnamed)",
    amountMinor: num(a.amountMinor),
  }));
  // Filter COGS out of operating expenses if both arrays contain it
  const operatingAccounts = expenseAccounts.filter(
    (a) => !cogsAccounts.some((c) => c.code === a.code),
  );

  const perBusiness =
    !businessId && data.perBusiness?.length ? data.perBusiness : null;

  // ─ Export ─

  const getCsvData = () => {
    const rows: Array<Array<string | number>> = [];
    rows.push(["REVENUE", ""]);
    revenueAccounts.forEach((a) =>
      rows.push([`  ${a.code} — ${a.name}`, format(a.amountMinor)]),
    );
    rows.push(["TOTAL REVENUE", format(revenueTotal)]);
    rows.push(["", ""]);
    if (cogsAccounts.length) {
      rows.push(["COST OF GOODS SOLD", ""]);
      cogsAccounts.forEach((a) =>
        rows.push([`  ${a.code} — ${a.name}`, format(a.amountMinor)]),
      );
      rows.push(["TOTAL COGS", format(cogsTotal)]);
      rows.push(["GROSS PROFIT", format(grossProfit)]);
      rows.push(["", ""]);
    }
    rows.push(["OPERATING EXPENSES", ""]);
    operatingAccounts.forEach((a) =>
      rows.push([`  ${a.code} — ${a.name}`, format(a.amountMinor)]),
    );
    rows.push(["TOTAL OPERATING EXPENSES", format(operatingTotal)]);
    rows.push(["", ""]);
    rows.push(["NET PROFIT", format(netProfit)]);
    return { headers: ["Account", "Amount"], rows };
  };

  const getPdfSections = () => {
    const sections: Array<{
      heading?: string;
      headers?: string[];
      rows?: Array<Array<string | number>>;
      totalRow?: { label: string; value: string };
    }> = [];

    sections.push({
      heading: "Summary",
      headers: ["Metric", "Amount"],
      rows: [
        ["Total Revenue", format(revenueTotal)],
        ["Cost of Goods Sold", format(cogsTotal)],
        ["Gross Profit", format(grossProfit)],
        ["Gross Margin", `${grossMargin}%`],
        ["Operating Expenses", format(operatingTotal)],
        ["Net Profit", format(netProfit)],
        ["Net Margin", `${netMargin}%`],
      ],
    });

    if (revenueAccounts.length) {
      sections.push({
        heading: "Revenue accounts",
        headers: ["Code", "Account", "Amount"],
        rows: revenueAccounts.map((a) => [
          a.code,
          a.name,
          format(a.amountMinor),
        ]),
        totalRow: { label: "Total Revenue", value: format(revenueTotal) },
      });
    }

    if (cogsAccounts.length) {
      sections.push({
        heading: "Cost of Goods Sold",
        headers: ["Code", "Account", "Amount"],
        rows: cogsAccounts.map((a) => [a.code, a.name, format(a.amountMinor)]),
        totalRow: { label: "Total COGS", value: format(cogsTotal) },
      });
    }

    if (operatingAccounts.length) {
      sections.push({
        heading: "Operating expenses",
        headers: ["Code", "Account", "Amount"],
        rows: operatingAccounts.map((a) => [
          a.code,
          a.name,
          format(a.amountMinor),
        ]),
        totalRow: {
          label: "Total Operating Expenses",
          value: format(operatingTotal),
        },
      });
    }

    if (perBusiness) {
      sections.push({
        heading: "Per-business breakdown",
        headers: ["Business", "Revenue", "Expenses", "Net Profit"],
        rows: perBusiness.map((b) => [
          b.businessName,
          format(num(b.revenueMinor)),
          format(num(b.expensesMinor)),
          format(num(b.netProfitMinor)),
        ]),
      });
    }

    return sections;
  };

  const getShareSummary = () =>
    [
      `Profit & Loss — ${scopeLabel}`,
      `${dateRangeLabel}`,
      ``,
      `Revenue:      ${format(revenueTotal)}`,
      `COGS:         ${format(cogsTotal)}`,
      `Gross Profit: ${format(grossProfit)} (${grossMargin}% margin)`,
      `Expenses:     ${format(operatingTotal)}`,
      `Net Profit:   ${format(netProfit)} (${netMargin}% margin)`,
    ].join("\n");

  // ─ Render ─

  return (
    <section className="space-y-5">
      <GroupCompletenessNotice meta={data as GroupReportMeta | undefined} />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <h2 className="text-base font-semibold text-foreground">
              Profit &amp; Loss
            </h2>
            <ScopeBadge label={scopeLabel} businessId={businessId} />
          </div>
          <p className="text-xs text-muted-foreground">
            {dateRangeLabel}
            {!businessId && perBusiness
              ? ` · ${perBusiness.length} business${perBusiness.length === 1 ? "" : "es"}`
              : ""}
          </p>
        </div>
        <ReportExportMenu
          title={`P&L — ${scopeLabel}`}
          subtitle={dateRangeLabel}
          getCsvData={getCsvData}
          getPdfSections={getPdfSections}
          getShareSummary={getShareSummary}
        />
      </div>

      {/* Headline cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HeadlineCard
          label="Revenue"
          value={format(revenueTotal)}
          tone="green"
        />
        <HeadlineCard
          label="COGS"
          value={format(cogsTotal)}
          tone="slate"
          hint={grossMargin ? `Gross margin ${grossMargin}%` : undefined}
        />
        <HeadlineCard
          label="Op. Expenses"
          value={format(operatingTotal)}
          tone="slate"
        />
        <HeadlineCard
          label="Net Profit"
          value={format(netProfit)}
          tone={isProfitable ? "green" : "rose"}
          icon={isProfitable ? TrendingUp : TrendingDown}
          hint={`${netMargin}% margin`}
        />
      </div>

      {revenueAccounts.length > 0 && (
        <AccountTable
          title="Revenue accounts"
          accounts={revenueAccounts}
          totalLabel="Total Revenue"
          totalMinor={revenueTotal}
          format={format}
        />
      )}

      {cogsAccounts.length > 0 && (
        <AccountTable
          title="Cost of Goods Sold"
          accounts={cogsAccounts}
          totalLabel="Total COGS"
          totalMinor={cogsTotal}
          format={format}
        />
      )}

      {operatingAccounts.length > 0 && (
        <AccountTable
          title="Operating expenses"
          accounts={operatingAccounts}
          totalLabel="Total Operating Expenses"
          totalMinor={operatingTotal}
          format={format}
        />
      )}

      {revenueAccounts.length === 0 &&
        cogsAccounts.length === 0 &&
        operatingAccounts.length === 0 && (
          <div className="rounded-md border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
            No activity in this period.
          </div>
        )}

      {perBusiness && (
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b bg-muted/40">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Building2 className="h-3.5 w-3.5" />
              Per-business breakdown
            </h3>
          </div>
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="font-medium py-2 px-4">Business</th>
                <th className="font-medium py-2 px-4 text-right">Revenue</th>
                <th className="font-medium py-2 px-4 text-right">Expenses</th>
                <th className="font-medium py-2 px-4 text-right">Net Profit</th>
              </tr>
            </thead>
            <tbody>
              {perBusiness.map((b) => (
                <tr key={b.businessId} className="border-b last:border-0">
                  <td className="py-2.5 px-4 font-medium">{b.businessName}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums">
                    {format(num(b.revenueMinor))}
                  </td>
                  <td className="py-2.5 px-4 text-right tabular-nums">
                    {format(num(b.expensesMinor))}
                  </td>
                  <td
                    className={`py-2.5 px-4 text-right tabular-nums font-medium ${num(b.netProfitMinor) < 0 ? "text-rose-600" : "text-[#4CAF50]"}`}
                  >
                    {format(num(b.netProfitMinor))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────

const ScopeBadge: React.FC<{
  label: string;
  businessId?: string | null;
}> = ({ label, businessId }) => (
  <span
    className={[
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
      businessId
        ? "bg-[#4CAF50]/10 text-[#4CAF50]"
        : "bg-muted text-muted-foreground",
    ].join(" ")}
  >
    <Building2 className="h-2.5 w-2.5" />
    {label}
  </span>
);

const HeadlineCard: React.FC<{
  label: string;
  value: string;
  hint?: string;
  tone?: "green" | "rose" | "slate";
  icon?: React.ElementType;
}> = ({ label, value, hint, tone = "slate", icon: Icon }) => {
  const valueColor =
    tone === "green"
      ? "text-[#4CAF50]"
      : tone === "rose"
        ? "text-rose-600"
        : "text-foreground";
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          {label}
        </div>
        {Icon && <Icon className={`h-3.5 w-3.5 ${valueColor}`} />}
      </div>
      <div className={`text-xl font-bold tabular-nums ${valueColor}`}>
        {value}
      </div>
      {hint && (
        <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>
      )}
    </div>
  );
};

const AccountTable: React.FC<{
  title: string;
  accounts: Array<{ code: string; name: string; amountMinor: number }>;
  totalLabel: string;
  totalMinor: number;
  format: (m: number) => string;
}> = ({ title, accounts, totalLabel, totalMinor, format }) => (
  <div className="rounded-lg border bg-card overflow-hidden">
    <div className="px-4 py-3 border-b bg-muted/40">
      <h3 className="text-sm font-semibold">{title}</h3>
    </div>
    <table className="w-full text-sm">
      <tbody>
        {accounts.map((a) => (
          <tr key={a.code} className="border-b last:border-0 hover:bg-muted/20">
            <td className="py-2 px-4 text-muted-foreground tabular-nums w-20">
              {a.code}
            </td>
            <td className="py-2 px-4">{a.name}</td>
            <td className="py-2 px-4 text-right tabular-nums">
              {format(a.amountMinor)}
            </td>
          </tr>
        ))}
        <tr className="bg-muted/30 font-semibold">
          <td className="py-2.5 px-4" />
          <td className="py-2.5 px-4">{totalLabel}</td>
          <td className="py-2.5 px-4 text-right tabular-nums">
            {format(totalMinor)}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default GroupPnLDetail;
